import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { ApiError } from '../../abstract/api-error';
import { DroneManufacturerService } from '../../core/data-services/drone-manufacturer.service';
import { DroneModelService } from '../../core/data-services/drone-model.service';
import { DroneModelRequest } from '../../core/request-models/drone-model-request.model';
import { optionalText } from '../../core/utils/form-value-utils';

@Component({
  selector: 'app-model-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './model-form.component.html',
  styleUrl: './model-form.component.scss'
})
export class ModelFormComponent implements OnInit {
  modelForm: FormGroup;
  loading = false;
  loadingModel = false;
  errorMessage = '';
  successMessage = '';

  isEditMode = false;
  modelId: string | null = null;
  manufacturerId: string | null = null;
  manufacturerName = '';

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private droneManufacturerService: DroneManufacturerService,
    private droneModelService: DroneModelService
  ) {
    this.modelForm = this.fb.group({
      modelName: ['', [Validators.required, Validators.minLength(1)]],
      description: [''],
      maxTakeoffWeightLbs: [null, [Validators.min(0), Validators.max(10000)]],
      imageUrl: ['', [Validators.pattern(/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([\/\w .-]*)*\/?$/)]]
    });
  }

  ngOnInit(): void {
    this.manufacturerId = this.route.snapshot.paramMap.get('manufacturerId');
    this.modelId = this.route.snapshot.paramMap.get('modelId');
    this.isEditMode = !!this.modelId;

    if (this.manufacturerId) {
      this.loadManufacturer();
    }

    if (this.isEditMode) {
      this.loadModel();
    }
  }

  async loadManufacturer(): Promise<void> {
    if (!this.manufacturerId) return;
    try {
      const manufacturer = await firstValueFrom(this.droneManufacturerService.getManufacturer(this.manufacturerId));
      this.manufacturerName = manufacturer?.name ?? '';
    } catch (error) {
      console.error('Failed to load manufacturer:', error);
    }
  }

  async loadModel(): Promise<void> {
    if (!this.modelId) return;
    this.loadingModel = true;

    try {
      const model = await firstValueFrom(this.droneModelService.getModel(this.modelId));
      if (model) {
        this.modelForm.patchValue({
          modelName: model.modelName ?? '',
          description: model.description ?? '',
          maxTakeoffWeightLbs: model.maxTakeoffWeightLbs ?? null,
          imageUrl: model.imageUrl ?? ''
        });
      }
    } catch (error) {
      console.error('Failed to load model:', error);
      this.errorMessage = 'Failed to load model details';
    } finally {
      this.loadingModel = false;
    }
  }

  async onSubmit(): Promise<void> {
    if (this.modelForm.invalid) {
      this.modelForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      const request = this.toRequest();

      if (this.isEditMode && this.modelId) {
        await firstValueFrom(this.droneModelService.updateModel(this.modelId, request));

        this.successMessage = 'Model updated successfully';

        setTimeout(() => {
          this.router.navigate(['/drone-catalog', this.manufacturerId]);
        }, 1500);
      } else {
        await firstValueFrom(this.droneModelService.createModel({ ...request, manufacturerId: this.manufacturerId ?? '' }));

        this.router.navigate(['/success'], {
          state: {
            entities: [
              {
                type: 'model',
                action: 'created',
                name: request.modelName,
                // Dynamic — go back to the parent manufacturer's detail page.
                listRoute: `/drone-catalog/${this.manufacturerId}`,
              },
            ],
            message: 'Drone model created successfully',
            details: {
              'Model Name': request.modelName,
              'Manufacturer': this.manufacturerName,
              'Max Takeoff Weight': request.maxTakeoffWeightLbs ? `${request.maxTakeoffWeightLbs} lbs` : 'Not specified'
            },
            createAnotherRoute: `/drone-catalog/${this.manufacturerId}/models/create`
          }
        });
      }
    } catch (error) {
      console.error('Save model error:', error);
      this.errorMessage = ApiError.fromHttpError(error).errors[0] ?? (this.isEditMode
        ? 'Failed to update model. Please try again.'
        : 'Failed to create model. Please try again.');
    } finally {
      this.loading = false;
    }
  }

  cancel(): void {
    this.router.navigate(['/drone-catalog', this.manufacturerId]);
  }

  /** The form as the hub's writable fields; an emptied optional field becomes `null` (clear). */
  private toRequest(): DroneModelRequest {
    const formData = this.modelForm.value;
    const weight = formData.maxTakeoffWeightLbs;
    return {
      modelName: formData.modelName,
      description: optionalText(formData.description),
      maxTakeoffWeightLbs: weight === '' || weight === null || weight === undefined ? null : parseFloat(weight),
      imageUrl: optionalText(formData.imageUrl),
    };
  }

  get pageTitle(): string {
    return this.isEditMode ? 'Edit Drone Model' : 'Add Drone Model';
  }

  get submitButtonText(): string {
    if (this.loading) {
      return this.isEditMode ? 'Saving...' : 'Creating...';
    }
    return this.isEditMode ? 'Save Changes' : 'Create Model';
  }
}
